import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';
import LoadingSpinner from './LoadingSpinner';
import UserAssistantComponent from './UserAssistantComponent';

const DomainsComponent = () => {
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainDetail, setDomainDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const detailRef = useRef(null);
  const [creatingItem, setCreatingItem] = useState(false);
  const [newItemData, setNewItemData] = useState({});
  const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);
  const [showCreateCustom, setShowCreateCustom] = useState(false);
  const [customAction, setCustomAction] = useState({
    name: '', description: '', icon: '📋', category: 'input', fields: []
  });
  const [newField, setNewField] = useState({
    name: '', field_type: 'text', required: false, options: ''
  });

  const domainPlaceholders = {
    'dom-001': 'Ej: Seguros, Prestaciones, Derechos Legales, Testamento...',
    'dom-002': 'Ej: Control de Servicios, Seguros del Hogar, Remodelacion...',
    'dom-003': 'Ej: Rutas Frecuentes, Seguro Vehicular, Peajes, Licencias...',
    'dom-004': 'Ej: Nomina, Evaluacion de Personal, Capacitaciones, Contratos...',
    'dom-005': 'Ej: Proveedores, Clientes, Acuerdos Comerciales, Facturas...',
    'dom-006': 'Ej: Historial Clinico, Vacunas, Alergias, Citas Medicas...',
    'dom-007': 'Ej: Cursos, Certificaciones, Horarios, Calificaciones...',
    'dom-008': 'Ej: Control de Ganado, Cosechas, Maquinaria, Insumos...',
    'dom-009': 'Ej: Deudas, Inversiones, Presupuesto Mensual, Ahorros...',
    'dom-010': 'Ej: Dispositivos, Licencias Software, Sensores IoT, Redes...',
    'dom-011': 'Ej: Mi Tienda, Lista de Deseos, Comparador de Precios...',
    'dom-012': 'Ej: Propiedades, Alquileres, Proyectos, Permisos de Obra...',
    'dom-013': 'Ej: Automatizacion, Flujo de Trabajo, Reglas Personalizadas...'
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${API}/domains/`);
      setDomains(response.data.domains);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDomainDetail = async (domainId) => {
    try {
      const response = await axios.get(`${API}/domains/${domainId}`);
      setDomainDetail(response.data);
    } catch (error) {
      console.error('Failed to fetch domain detail:', error);
    }
  };

  const handleDomainClick = (domain) => {
    setSelectedDomain(domain);
    fetchDomainDetail(domain.id);
    setSelectedCatalogItem(null);
    setNewItemData({});
    setTimeout(() => {
      detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    if (!selectedCatalogItem) return;
    
    setCreatingItem(true);
    try {
      await axios.post(`${API}/items/`, {
        item_catalog_id: selectedCatalogItem.id,
        data: newItemData
      });
      
      // Refresh domain detail
      fetchDomainDetail(selectedDomain.id);
      setNewItemData({});
      setSelectedCatalogItem(null);
      alert('¡Item creado exitosamente!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear item');
    } finally {
      setCreatingItem(false);
    }
  };

  const handleAddField = () => {
    if (!newField.name.trim()) return;
    const field = {
      name: newField.name.trim().toLowerCase().replace(/\s+/g, '_'),
      field_type: newField.field_type,
      required: newField.required
    };
    if (newField.field_type === 'select' && newField.options.trim()) {
      field.options = newField.options.split(',').map(o => o.trim()).filter(Boolean);
    }
    setCustomAction(prev => ({ ...prev, fields: [...prev.fields, field] }));
    setNewField({ name: '', field_type: 'text', required: false, options: '' });
  };

  const handleRemoveField = (idx) => {
    setCustomAction(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== idx)
    }));
  };

  const handleCreateCustomAction = async () => {
    if (!customAction.name.trim()) {
      alert('El nombre es obligatorio');
      return;
    }
    try {
      await axios.post(`${API}/items/catalog/custom`, {
        domain_id: selectedDomain.id,
        name: customAction.name,
        description: customAction.description,
        icon: customAction.icon,
        fields: customAction.fields,
        category: customAction.category
      });
      fetchDomainDetail(selectedDomain.id);
      setShowCreateCustom(false);
      setCustomAction({ name: '', description: '', icon: '📋', category: 'input', fields: [] });
      alert('Cual-Accion creada exitosamente');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear Cual-Accion');
    }
  };

  const handleDeleteCustomAction = async (itemId) => {
    if (!window.confirm('¿Eliminar esta Cual-Accion personalizada?')) return;
    try {
      await axios.delete(`${API}/items/catalog/custom/${itemId}`);
      fetchDomainDetail(selectedDomain.id);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const renderFieldInput = (field) => {
    const value = newItemData[field.name] || '';
    
    switch (field.field_type) {
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => setNewItemData({...newItemData, [field.name]: e.target.value})}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            rows={3}
            required={field.required}
          />
        );
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => setNewItemData({...newItemData, [field.name]: e.target.value})}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            required={field.required}
          >
            <option value="">Seleccionar...</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => setNewItemData({...newItemData, [field.name]: parseFloat(e.target.value) || ''})}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            required={field.required}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => setNewItemData({...newItemData, [field.name]: e.target.value})}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            required={field.required}
          />
        );
      case 'boolean':
        return (
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) => setNewItemData({...newItemData, [field.name]: e.target.checked})}
            className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
          />
        );
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => setNewItemData({...newItemData, [field.name]: e.target.value})}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
            required={field.required}
          />
        );
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6" ref={detailRef}>
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Mis Dominios CUALAPP</h2>
        <p className="text-gray-600">Los 13 Dominios de tu vida digital</p>
      </div>

      {/* Domain Detail Panel - ABOVE the grid when active */}
      {selectedDomain && domainDetail && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div 
            className="p-4 sm:p-6 text-white"
            style={{ backgroundColor: selectedDomain.color }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-4xl">{selectedDomain.icon}</span>
                <div>
                  <h3 className="text-xl font-bold">{selectedDomain.name}</h3>
                  <p className="text-sm opacity-90">{selectedDomain.description}</p>
                </div>
              </div>
              <button 
                onClick={() => { setSelectedDomain(null); setDomainDetail(null); }}
                data-testid="domain-close-btn"
                className="text-white/80 hover:text-white bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
              >
                Volver a Dominios
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Asistente CUAL - Only in "Programando Mis Dominios" */}
            {selectedDomain.id === 'dom-013' && (
              <div className="mb-6" data-testid="domain-assistant-section">
                <UserAssistantComponent />
              </div>
            )}

            {/* Items Catalog */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900">
                  Cual-Acciones Disponibles ({domainDetail.items_catalog?.length || 0})
                </h4>
                <button
                  onClick={() => setShowCreateCustom(!showCreateCustom)}
                  data-testid="create-custom-action-btn"
                  className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-all bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 shadow-sm"
                >
                  <span>{showCreateCustom ? '✕' : '+'}</span>
                  <span>{showCreateCustom ? 'Cerrar' : 'Nueva Cual-Accion'}</span>
                </button>
              </div>

              {/* Create Custom Cual-Accion Form */}
              {showCreateCustom && (
                <div className="mb-4 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200" data-testid="custom-action-form">
                  <h5 className="font-semibold text-gray-800 mb-3">Crear nueva Cual-Accion para "{selectedDomain.name}"</h5>
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                        <input
                          type="text"
                          value={customAction.name}
                          onChange={(e) => setCustomAction(prev => ({ ...prev, name: e.target.value }))}
                          placeholder={domainPlaceholders[selectedDomain?.id] || 'Ej: Nombre de tu accion personalizada...'}
                          data-testid="custom-action-name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Icono</label>
                        <input
                          type="text"
                          value={customAction.icon}
                          onChange={(e) => setCustomAction(prev => ({ ...prev, icon: e.target.value }))}
                          data-testid="custom-action-icon"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
                      <input
                        type="text"
                        value={customAction.description}
                        onChange={(e) => setCustomAction(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Descripcion breve de esta accion..."
                        data-testid="custom-action-description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                      <select
                        value={customAction.category}
                        onChange={(e) => setCustomAction(prev => ({ ...prev, category: e.target.value }))}
                        data-testid="custom-action-category"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value="input">Entrada (registrar datos)</option>
                        <option value="output">Salida (generar/exportar)</option>
                      </select>
                    </div>

                    {/* Dynamic Fields Builder */}
                    <div className="border-t border-emerald-200 pt-3">
                      <h6 className="text-sm font-semibold text-gray-700 mb-2">Campos personalizados</h6>
                      {customAction.fields.length > 0 && (
                        <div className="space-y-1 mb-3">
                          {customAction.fields.map((f, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-gray-200 text-sm">
                              <span>
                                <span className="font-medium">{f.name}</span>
                                <span className="text-gray-400 mx-1">|</span>
                                <span className="text-gray-500">{f.field_type}</span>
                                {f.required && <span className="text-red-500 ml-1">*</span>}
                                {f.options && <span className="text-gray-400 ml-1">({f.options.join(', ')})</span>}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveField(idx)}
                                className="text-red-400 hover:text-red-600 ml-2"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 items-end">
                        <div className="flex-1 min-w-[120px]">
                          <label className="block text-xs text-gray-500">Nombre del campo</label>
                          <input
                            type="text"
                            value={newField.name}
                            onChange={(e) => setNewField(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Ej: fecha_inicio"
                            data-testid="new-field-name"
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-emerald-500"
                          />
                        </div>
                        <div className="w-28">
                          <label className="block text-xs text-gray-500">Tipo</label>
                          <select
                            value={newField.field_type}
                            onChange={(e) => setNewField(prev => ({ ...prev, field_type: e.target.value }))}
                            data-testid="new-field-type"
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-emerald-500"
                          >
                            <option value="text">Texto</option>
                            <option value="number">Numero</option>
                            <option value="date">Fecha</option>
                            <option value="textarea">Texto largo</option>
                            <option value="boolean">Si/No</option>
                            <option value="select">Opciones</option>
                          </select>
                        </div>
                        {newField.field_type === 'select' && (
                          <div className="flex-1 min-w-[140px]">
                            <label className="block text-xs text-gray-500">Opciones (separadas por coma)</label>
                            <input
                              type="text"
                              value={newField.options}
                              onChange={(e) => setNewField(prev => ({ ...prev, options: e.target.value }))}
                              placeholder="Opcion1, Opcion2..."
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-emerald-500"
                            />
                          </div>
                        )}
                        <label className="flex items-center text-sm text-gray-600 gap-1">
                          <input
                            type="checkbox"
                            checked={newField.required}
                            onChange={(e) => setNewField(prev => ({ ...prev, required: e.target.checked }))}
                            className="rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                          />
                          Requerido
                        </label>
                        <button
                          type="button"
                          onClick={handleAddField}
                          data-testid="add-field-btn"
                          className="px-3 py-1.5 bg-white border border-emerald-300 text-emerald-700 rounded text-sm hover:bg-emerald-50 font-medium"
                        >
                          + Campo
                        </button>
                      </div>
                    </div>

                    <div className="flex space-x-3 pt-2">
                      <button
                        type="button"
                        onClick={handleCreateCustomAction}
                        data-testid="save-custom-action-btn"
                        className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-2 px-4 rounded-lg hover:from-emerald-600 hover:to-teal-600 font-medium transition-all shadow-sm"
                      >
                        Crear Cual-Accion
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateCustom(false);
                          setCustomAction({ name: '', description: '', icon: '📋', category: 'input', fields: [] });
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {domainDetail.items_catalog?.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setSelectedCatalogItem(item);
                      setNewItemData({});
                    }}
                    data-testid={`catalog-item-${item.id}`}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      selectedCatalogItem?.id === item.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{item.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900">{item.name}</p>
                          {item.is_custom && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Personalizada</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{item.description}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          {item.is_free ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">GRATIS</span>
                          ) : (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{item.price_cual} CUAL</span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            item.category === 'input' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-orange-100 text-orange-700'
                          }`}>
                            {item.category === 'input' ? 'Entrada' : 'Salida'}
                          </span>
                        </div>
                      </div>
                      {item.is_custom && (
                        <span
                          onClick={(e) => { e.stopPropagation(); handleDeleteCustomAction(item.id); }}
                          className="text-gray-400 hover:text-red-500 cursor-pointer text-lg"
                          title="Eliminar"
                        >
                          ✕
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Create Item Form */}
            {selectedCatalogItem && (
              <div className="border-t pt-6">
                <h4 className="font-semibold text-gray-900 mb-3">
                  Crear: {selectedCatalogItem.name}
                </h4>
                <form onSubmit={handleCreateItem} className="space-y-4">
                  {selectedCatalogItem.fields?.map((field) => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-gray-700 capitalize">
                        {field.name.replace(/_/g, ' ')}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.description && (
                        <p className="text-xs text-gray-500 mb-1">{field.description}</p>
                      )}
                      {renderFieldInput(field)}
                    </div>
                  ))}
                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      disabled={creatingItem}
                      data-testid="create-item-btn"
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 px-4 rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 transition-all"
                    >
                      {creatingItem ? 'Creando...' : 'Crear Cual-Acción'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCatalogItem(null);
                        setNewItemData({});
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* User's Items */}
            {domainDetail.user_items?.length > 0 && (
              <div className="border-t pt-6 mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">
                  Mis Cual-Acciones ({domainDetail.user_items_count})
                </h4>
                <div className="space-y-3">
                  {domainDetail.user_items.map((item) => {
                    const catalogItem = domainDetail.items_catalog?.find(c => c.id === item.item_catalog_id);
                    return (
                      <div key={item.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-xl">{catalogItem?.icon || '📦'}</span>
                              <p className="font-medium text-gray-900">
                                {item.custom_name || catalogItem?.name || 'Item'}
                              </p>
                            </div>
                            <div className="mt-2 text-sm text-gray-600">
                              {Object.entries(item.data || {}).slice(0, 3).map(([key, value]) => (
                                <p key={key} className="truncate">
                                  <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span> {String(value)}
                                </p>
                              ))}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">
                            {new Date(item.created_at).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Domains Grid */}
      <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 ${selectedDomain ? 'opacity-90' : ''}`}>
        {domains.map((domain) => (
          <button
            key={domain.id}
            onClick={() => handleDomainClick(domain)}
            data-testid={`domain-card-${domain.id}`}
            className={`p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-lg hover:scale-105 ${
              selectedDomain?.id === domain.id
                ? 'border-purple-500 shadow-lg bg-purple-50 ring-2 ring-purple-300'
                : 'border-gray-200 bg-white hover:border-purple-300'
            }`}
            style={{
              borderLeftColor: domain.color,
              borderLeftWidth: '4px'
            }}
          >
            <div className="text-3xl mb-2">{domain.icon}</div>
            <h3 className="font-semibold text-gray-900 text-sm">{domain.name}</h3>
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{domain.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
};



export default DomainsComponent;
